import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useSavedReplies } from './useSavedReplies'
import { toast } from '@buffer-mono/popcorn'

import type { SavedReplyId } from '../../types'
import type { SavedReply } from '~publish/gql/graphql'

type UseAddSavedReplyFormReturn = {
  name: string | null
  setName: (name: string | null) => void
  content: string
  setContent: (content: string) => void
  onSubmit: () => void
  onDelete: (id: SavedReplyId) => Promise<void>
  onUpdate: (id: SavedReplyId) => Promise<void>
  discardDialogVisible: boolean
  showDiscardDialog: (open: boolean) => void
  handleDiscard: () => void
  handleKeepWriting: () => void
  formRef: RefObject<HTMLFormElement>
}

type UseAddSavedReplyFormProps = {
  isEditMode: boolean
  hasDataChanged?: boolean
  savedReply?: SavedReply
  setOpen?: (open: boolean) => void
  open?: boolean
}

export const useAddSavedReplyForm = ({
  isEditMode,
  hasDataChanged,
  savedReply,
  setOpen,
  open,
}: UseAddSavedReplyFormProps): UseAddSavedReplyFormReturn => {
  const { createSavedReply, deleteSavedReply, updateSavedReply } =
    useSavedReplies()
  const [name, setName] = useState<string | null>(null)
  const [content, setContent] = useState('')
  const [discardDialogVisible, showDiscardDialog] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  // When entering edit mode or the saved reply changes, update the form values
  useEffect(() => {
    if (isEditMode && open && savedReply) {
      setName(savedReply.title ?? '')
      setContent(savedReply.content.text ?? '')
    } else if (!isEditMode && open) {
      setName('')
      setContent('')
    }
  }, [isEditMode, open, savedReply])
  /*

   * Reset the form
   */
  const resetForm = useCallback((): void => {
    setName(null)
    setContent('')
    showDiscardDialog(false)
    setOpen?.(false)
  }, [setName, setContent, showDiscardDialog, setOpen])

  // Handle clicks outside the form
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (!open) return

      const clickedOutsideForm =
        formRef.current && !formRef.current.contains(event.target as Node)

      const formHasChanged = isEditMode
        ? hasDataChanged
        : Boolean(name?.trim()) || Boolean(content?.trim())

      if (clickedOutsideForm) {
        if (formHasChanged) {
          showDiscardDialog(true)
        } else {
          // Reset the form
          resetForm()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [
    content,
    discardDialogVisible,
    hasDataChanged,
    isEditMode,
    name,
    resetForm,
    open,
  ])

  /**
   * Creates a new saved reply
   */
  const onSubmit = async (): Promise<void> => {
    try {
      await createSavedReply({
        title: name?.trim(),
        content: content.trim(),
      })
      toast.success('Saved reply created')
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to create saved reply, please try again later or contact support',
      )
    }
  }

  /**
   *
   * Deletes a saved reply
   */
  const onDelete = async (id: SavedReplyId): Promise<void> => {
    try {
      await deleteSavedReply({ id })
      toast.success('Saved reply deleted')
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to delete saved reply, please try again later or contact support',
      )
    }
  }

  /**
   * Updates a saved reply
   */
  const onUpdate = async (id: SavedReplyId): Promise<void> => {
    try {
      await updateSavedReply({
        id,
        title: name?.trim(),
        content: content.trim(),
      })
      toast.success('Saved reply updated')
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update the saved reply, please try again later or contact support',
      )
    }
  }

  /**
   * Discard changes in the form
   */
  const handleDiscard = useCallback((): void => {
    resetForm()
  }, [resetForm])

  /**
   * Keep writing in the form
   */
  const handleKeepWriting = useCallback((): void => {
    showDiscardDialog(false)
  }, [showDiscardDialog])

  return {
    name,
    setName,
    content,
    setContent,
    onSubmit,
    onDelete,
    onUpdate,
    discardDialogVisible,
    showDiscardDialog,
    handleDiscard,
    handleKeepWriting,
    formRef,
  }
}
